
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Drawer, Avatar, IconButton, Tooltip, Menu, MenuItem, Typography, Badge, Button } from '@mui/material';
import Scrollbar from 'src/components/Scrollbar';
import NavSection from 'src/components/NavSection';
import UserInfo from 'src/utils/Authorization/UserInfo';
import getSideBarConfig from './SidebarConfig';
import { LogoutOutlined, SwapHoriz, HelpOutline, SchoolOutlined } from '@mui/icons-material';
import { apiInstance } from 'src/utils/apiAuth';
import { useAuth } from 'src/utils/Authorization/AuthContext';
import usePushNotification from 'src/hooks/usePushNotification';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';

const DRAWER_WIDTH = 80;

const RootStyle = styled('div')(({ theme }) => ({
	flexShrink: 0,
	width: DRAWER_WIDTH
}));

export default function DashboardSidebar() {
	const navigate = useNavigate();
	const auth = useAuth();
	const userInfo = UserInfo();
	const [anchorEl, setAnchorEl] = useState(null);
	const active_role = userInfo?.active_role;

	// Super-admin-controlled sidebar visibility (managed from Django admin).
	// We only ever HIDE items: a nav item is dropped when the backend has a
	// SidebarOption row with is_visible=false for its path. Fail-open — if the
	// call fails or an item has no row, it stays visible (empty hidden set).
	const [hiddenSidebarKeys, setHiddenSidebarKeys] = useState(() => new Set());
	const [sidebarLabels, setSidebarLabels] = useState(() => new Map());

	useEffect(() => {
		let cancelled = false;
		apiInstance
			.get('sidebar-options/')
			.then((res) => {
				const options = res?.data?.data || [];
				const hidden = new Set(
					options.filter((o) => o && o.is_visible === false).map((o) => o.key)
				);
				const labels = new Map(
					options
						.filter((o) => o && typeof o.label === 'string' && o.label.trim() !== '')
						.map((o) => [o.key, o.label.trim()])
				);
				if (!cancelled) { setHiddenSidebarKeys(hidden); setSidebarLabels(labels); }
			})
			.catch(() => {
				// Fail-open: keep the full sidebar if the endpoint is unreachable.
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const sidebarConfig = getSideBarConfig(userInfo)
		.filter((item) => !hiddenSidebarKeys.has(item.path))
		.map((item) => ({ ...item, title: sidebarLabels.get(item.path) || item.title }));

	const notifications = usePushNotification();

	const handleAvatarClick = (event) => {
		if (userInfo?.role && userInfo.role.length > 1) {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleRoleChange = (role) => {
		userInfo.switchActiveRole(role);
		handleClose();
		navigate('/dashboard', { replace: true });
	};

	const handleLogout = () => {
		auth.logout();
		localStorage.clear();
		navigate('/login', { replace: true });
	};

	const handleTestNotification = async () => {
		try {
			await notifications.sendTestNotification();
			showToast('Test notification sent successfully!', 'success');
		} catch (error) {
			showErrorToast(error.message || 'Failed to send test notification');
		}
	};

	const renderContent = (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			<Scrollbar
				sx={{
					flex: 1,
					'& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' }
				}}
			>
				<Box sx={{ px: 1, py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
					<Tooltip title={userInfo?.role?.length > 1 ? "Switch Role" : ""} placement="right" arrow>
						<IconButton 
							onClick={handleAvatarClick}
							sx={{ 
								p: 0,
								cursor: userInfo?.role?.length > 1 ? 'pointer' : 'default'
							}}
						>
							<Badge
								badgeContent={
									userInfo?.role?.length > 1 ? (
										<SwapHoriz sx={{ fontSize: 14, color: 'white' }} />
									) : null
								}
								color="primary"
								overlap="circular"
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
								sx={{
									'& .MuiBadge-badge': {
										width: 20,
										height: 20,
										borderRadius: '50%',
										border: '2px solid white'
									}
								}}
							>
								<Avatar 
									src="/static/mock-images/avatars/avatar_default.jpg" 
									alt="photoURL" 
									sx={{ width: 40, height: 40 }} 
								/>
							</Badge>
						</IconButton>
					</Tooltip>
					<Typography 
						variant="caption" 
						sx={{ 
							mt: 0.5,
							fontSize: '0.65rem',
							fontWeight: 600,
							color: 'text.primary',
							textAlign: 'center',
							lineHeight: 1.2,
							wordBreak: 'break-word',
							width: '100%'
						}}
					>
						{userInfo?.first_name || userInfo?.username || 'User'}
					</Typography>
				</Box>
				<Menu
					anchorEl={anchorEl}
					open={Boolean(anchorEl)}
					onClose={handleClose}
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left',
					}}
					PaperProps={{
						sx: { minWidth: 150 }
					}}
				>
					<Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
						<Typography variant="caption" sx={{ color: 'text.secondary' }}>
							Switch Role
						</Typography>
					</Box>
					{userInfo?.role?.map((role, index) => (
						<MenuItem 
							key={index} 
							onClick={() => handleRoleChange(role)}
							selected={role === active_role}
							sx={{ fontSize: '0.875rem' }}
						>
							{role.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ")}
						</MenuItem>
					))}
				</Menu>

				<NavSection navConfig={sidebarConfig} />
			</Scrollbar>

			<Box sx={{ borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1 }}>
				{/* Test Notification Button */}
				{/* <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
					<Tooltip title="Send test notification" placement="right" arrow>
						<Button 
							variant="outlined" 
							color="primary" 
							size="small"
							onClick={handleTestNotification}
							disabled={notifications.loading}
							sx={{ 
								fontSize: '0.65rem',
								py: 0.5,
								px: 1,
								minWidth: 'auto'
							}}
						>
							Test
						</Button>
					</Tooltip>
				</Box> */}
				{/* Logout Button */}
				<Box sx={{ p: 1, pt: 0 }}>
					<Tooltip title="Tutorials" placement="right" arrow>
						<IconButton 
							onClick={() => navigate('/tutorials')}
							sx={{ 
								width: '100%', 
								height: 48,
								borderRadius: 1,
								color: 'secondary.main',
								'&:hover': { bgcolor: 'secondary.lighter' }
							}}
						>
							<SchoolOutlined />
						</IconButton>
					</Tooltip>
					<Tooltip title="Support" placement="right" arrow>
					<IconButton 
						onClick={() => navigate('/support')}
						sx={{ 
							width: '100%', 
							height: 48,
							borderRadius: 1,
							color: 'primary.main',
							'&:hover': { bgcolor: 'primary.lighter' }
						}}
					>
						<HelpOutline />
					</IconButton>
				</Tooltip>
				<Tooltip title="Logout" placement="right" arrow>
						<IconButton
							onClick={handleLogout}
							sx={{
								width: '100%',
								height: 48,
								borderRadius: 1,
								color: 'error.main',
								'&:hover': { bgcolor: 'error.lighter' }
							}}
						>
							<LogoutOutlined />
						</IconButton>
					</Tooltip>
				</Box>
			</Box>
		</Box>
	);

	return (
		<RootStyle id='drawer-sidebar'>
			<Drawer
				open
				variant="persistent"
				PaperProps={{
					sx: {
						width: DRAWER_WIDTH,
						bgcolor: 'background.default'
					}
				}}
			>
				{renderContent}
			</Drawer>
		</RootStyle>
	);
}

